import React, { useState, useEffect } from 'react';
import { contentApi } from '../../services/api';
import DeleteReasonModal from '../shared/DeleteReasonModal';
import RowActionButtons from '../shared/RowActionButtons';

// List of known universities for autocomplete (shared with PostList.js)
const UNIVERSITIES = [
  'Universität Hamburg',
  'TU Hamburg',
  'HafenCity Universität',
  'HAW Hamburg',
  'Bucerius Law School',
  'HfBK Hamburg',
  'Helmut-Schmidt-Universität',
  'Kühne Logistics University',
  'Hochschule für Musik und Theater',
  'Muthesius Kunsthochschule',
  'Christian-Albrechts-Universität Kiel',
  'HAW Kiel',
  'TH Lübeck',
  'Hochschule Flensburg',
  'Musikhochschule Lübeck',
  'HTW Berlin',
  'TU Berlin',
  'LMU Munich',
  'TUM',
  'RWTH Aachen',
  'University of Heidelberg',
  'University of Bonn',
  'University of Munich',
];

function CommentList({ onCommentSelect }) {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Quick filter for reported comments (Issue #157)
  const [reportedOnly, setReportedOnly] = useState(false);

  // Issue #160: Filter state
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [filters, setFilters] = useState({
    authorName: '',
    enrolledUniversity: '',
    dateFrom: '',
    dateTo: ''
  });
  const [tempFilters, setTempFilters] = useState({ ...filters });

  // University autocomplete state
  const [showUniversitySuggestions, setShowUniversitySuggestions] = useState(false);
  const [universitySuggestions, setUniversitySuggestions] = useState([]);

  const perPage = 20;

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const fetchComments = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (reportedOnly) params.reported_only = true;
      if (filters.authorName) params.author_name = filters.authorName;
      if (filters.enrolledUniversity) params.enrolled_university = filters.enrolledUniversity;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      const response = await contentApi.listComments(params);
      setComments(response.data.comments);
      setTotal(response.data.total);
    } catch (err) {
      setError('Fehler beim Laden der Kommentare');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchComments(); }, [page, reportedOnly, filters]);

  const handleToggleReportedOnly = () => {
    setReportedOnly(!reportedOnly);
    setPage(1);
  };

  const handleOpenFilterPopover = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    setShowFilterPopover(false);
  };

  const handleCancelFilters = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(false);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      authorName: '',
      enrolledUniversity: '',
      dateFrom: '',
      dateTo: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setReportedOnly(false);
    setPage(1);
  };

  // Handle university input change with autocomplete
  const handleUniversityInputChange = (value) => {
    setTempFilters({ ...tempFilters, enrolledUniversity: value });
    if (value.length > 0) {
      const filtered = UNIVERSITIES.filter(uni =>
        uni.toLowerCase().includes(value.toLowerCase())
      );
      setUniversitySuggestions(filtered);
      setShowUniversitySuggestions(filtered.length > 0);
    } else {
      setUniversitySuggestions([]);
      setShowUniversitySuggestions(false);
    }
  };

  const handleUniversitySelect = (university) => {
    setTempFilters({ ...tempFilters, enrolledUniversity: university });
    setShowUniversitySuggestions(false);
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchComments(); };

  // Issue #408: Delete confirmation with documented reason
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const openDeleteModal = (commentId) => {
    setError('');
    setSuccessMessage('');
    setDeleteTargetId(commentId);
  };
  const closeDeleteModal = () => setDeleteTargetId(null);

  const handleDeleteConfirm = async (reason) => {
    setError('');
    setSuccessMessage('');
    try {
      await contentApi.deleteComment(deleteTargetId, reason);
      setSuccessMessage('Kommentar gelöscht');
      setDeleteTargetId(null);
      fetchComments();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      if (err.response?.data?.error === 'already_deleted') {
        setError('Kommentar ist bereits gelöscht');
        setDeleteTargetId(null);
      } else {
        // Re-throw so the modal can display its own error (e.g. reason_too_short)
        throw err;
      }
    }
  };

  const isDeleted = (comment) => comment.deleted_at != null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Kommentare</h1>
        <p className="page-subtitle">{total} Kommentare insgesamt</p>
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Inhalt suchen..." className="search-input" />
          <button type="submit" className="search-button">Suchen</button>
        </form>

        <button
          type="button"
          className={`quick-filter-button ${reportedOnly ? 'active' : ''}`}
          onClick={handleToggleReportedOnly}
        >
          Gemeldete Kommentare
        </button>

        <div className="filter-controls">
          <button
            type="button"
            className="filter-button"
            onClick={handleOpenFilterPopover}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Filter
            {activeFilterCount > 0 && (
              <span className="filter-count-badge">{activeFilterCount}</span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="reset-filter-button"
              onClick={handleResetFilters}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Filter Popover (Issue #160) */}
      {showFilterPopover && (
        <div className="filter-popover-overlay" onClick={handleCancelFilters}>
          <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
            <div className="filter-popover-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <span className="filter-title">Filter</span>
            </div>

            <div className="filter-popover-content">
              {/* Author Name */}
              <div className="filter-section">
                <label className="filter-section-label">Name des Autors</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Vor- oder Nachname eingeben..."
                  value={tempFilters.authorName}
                  onChange={(e) => setTempFilters({ ...tempFilters, authorName: e.target.value })}
                />
              </div>

              {/* Enrolled University */}
              <div className="filter-section">
                <label className="filter-section-label">Hochschule des Autors</label>
                <div className="autocomplete-container">
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="z.B. TU Hamburg, HTW Berlin..."
                    value={tempFilters.enrolledUniversity}
                    onChange={(e) => handleUniversityInputChange(e.target.value)}
                    onFocus={() => {
                      if (tempFilters.enrolledUniversity.length > 0) {
                        const filtered = UNIVERSITIES.filter(uni =>
                          uni.toLowerCase().includes(tempFilters.enrolledUniversity.toLowerCase())
                        );
                        setUniversitySuggestions(filtered);
                        setShowUniversitySuggestions(filtered.length > 0);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow click on suggestion
                      setTimeout(() => setShowUniversitySuggestions(false), 200);
                    }}
                  />
                  {showUniversitySuggestions && universitySuggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {universitySuggestions.map((uni, index) => (
                        <li
                          key={index}
                          className="autocomplete-suggestion"
                          onClick={() => handleUniversitySelect(uni)}
                        >
                          {uni}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Date Range */}
              <div className="filter-section">
                <label className="filter-section-label">Zeitraum</label>
                <div className="date-range-inputs">
                  <div className="date-input-group">
                    <label className="date-label">Von</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.dateFrom}
                      onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="date-input-group">
                    <label className="date-label">Bis</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.dateTo}
                      onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="filter-popover-buttons">
                <button
                  type="button"
                  className="filter-cancel-button"
                  onClick={handleCancelFilters}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="filter-apply-button"
                  onClick={handleApplyFilters}
                >
                  Anwenden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {isLoading ? (
        <div className="loading-container"><div className="loading-spinner"></div></div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Autor</th>
                  <th>Hochschule</th>
                  <th>Inhalt</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {comments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-table-message">
                      {activeFilterCount > 0 ? 'Keine Kommentare gefunden mit den gewählten Filtern' : (reportedOnly ? 'Keine gemeldeten Kommentare gefunden' : 'Keine Kommentare vorhanden')}
                    </td>
                  </tr>
                ) : (
                  comments.map((comment) => (
                    <tr
                      key={comment.id}
                      className={(comment.report_count || 0) >= 5 ? 'reported-row' : ''}
                    >
                      <td>
                        {comment.is_anonymous ? <em>Anonym</em> : comment.author_name}
                        <br /><small>{comment.author_email}</small>
                      </td>
                      <td>{comment.author_university || '-'}</td>
                      <td className="content-cell">{comment.content.substring(0, 150)}{comment.content.length > 150 && '...'}</td>
                      <td>{formatDate(comment.created_at)}</td>
                      <td>
                        {/* Issue #410: Detail link is available for comments
                            auto-deleted due to 5 or more reports. */}
                        <RowActionButtons
                          onView={
                            (comment.report_count || 0) >= 5 && onCommentSelect
                              ? () => onCommentSelect(comment.id)
                              : null
                          }
                          onDelete={
                            !isDeleted(comment)
                              ? () => openDeleteModal(comment.id)
                              : null
                          }
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="pagination-btn">← Zurück</button>
              <span className="pagination-info">Seite {page} von {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="pagination-btn">Weiter →</button>
            </div>
          )}
        </>
      )}

      {/* Issue #408: Delete-with-reason modal */}
      <DeleteReasonModal
        open={deleteTargetId !== null}
        title="Kommentar löschen"
        description="Sie sind dabei, diesen Kommentar zu löschen. Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDeleteConfirm}
        onCancel={closeDeleteModal}
      />
    </div>
  );
}

export default CommentList;
